import type { Skill } from '../domain.js';

/**
 * Українська мова та читання, 3–6 клас НУШ.
 *
 * Частина навичок процедурна (орфографія — генерується зі словника з
 * позначками), частина живе в курованому банку (читання з розумінням,
 * література), бо потребує авторського тексту.
 */
export const UKRAINIAN_SKILLS: Skill[] = [
  // ── Фонетика ────────────────────────────────────────────────────────────
  {
    id: 'ukrainian.phonetics.sounds_letters',
    subject: 'ukrainian',
    strand: 'phonetics',
    title: 'Звуки і букви',
    description: 'Розрізняє звуки й букви, рахує їх у слові, знає алфавіт.',
    gradeBand: [2, 4],
    prerequisites: [],
    baseDifficulty: 860,
    foundational: true,
    generators: ['ukr.soundsLetters'],
  },
  {
    id: 'ukrainian.phonetics.syllables_stress',
    subject: 'ukrainian',
    strand: 'phonetics',
    title: 'Склади й наголос',
    description: 'Ділить слова на склади, правильно ставить наголос.',
    gradeBand: [2, 6],
    prerequisites: ['ukrainian.phonetics.sounds_letters'],
    baseDifficulty: 950,
    generators: ['ukr.stress'],
  },

  // ── Орфографія ──────────────────────────────────────────────────────────
  {
    id: 'ukrainian.spelling.apostrophe',
    subject: 'ukrainian',
    strand: 'spelling',
    title: 'Апостроф',
    description: 'Знає правила вживання апострофа й застосовує їх.',
    gradeBand: [3, 6],
    prerequisites: ['ukrainian.phonetics.sounds_letters'],
    baseDifficulty: 1010,
    generators: ['ukr.apostrophe'],
  },
  {
    id: 'ukrainian.spelling.soft_sign',
    subject: 'ukrainian',
    strand: 'spelling',
    title: 'М\'який знак',
    description: 'Правильно вживає м\'який знак, зокрема перед о та в суфіксах.',
    gradeBand: [3, 6],
    prerequisites: ['ukrainian.phonetics.sounds_letters'],
    baseDifficulty: 1000,
    generators: ['ukr.softSign'],
  },
  {
    id: 'ukrainian.spelling.unstressed_vowels',
    subject: 'ukrainian',
    strand: 'spelling',
    title: 'Ненаголошені е — и',
    description: 'Перевіряє ненаголошені голосні наголосом або запам\'ятовує.',
    gradeBand: [3, 6],
    prerequisites: ['ukrainian.phonetics.syllables_stress'],
    baseDifficulty: 1040,
    generators: ['ukr.unstressedVowels'],
  },
  {
    id: 'ukrainian.spelling.doubling',
    subject: 'ukrainian',
    strand: 'spelling',
    title: 'Подвоєння приголосних',
    description: 'Пише подвоєні приголосні в іменниках і прикметниках.',
    gradeBand: [4, 6],
    prerequisites: ['ukrainian.spelling.soft_sign'],
    baseDifficulty: 1120,
    generators: ['ukr.doubling'],
  },
  {
    id: 'ukrainian.spelling.prefixes',
    subject: 'ukrainian',
    strand: 'spelling',
    title: 'Префікси з-, с-, роз-, без-',
    description: 'Обирає правильний префікс за наступним приголосним.',
    gradeBand: [4, 6],
    prerequisites: ['ukrainian.morphology.word_formation'],
    baseDifficulty: 1090,
    generators: ['ukr.prefixes'],
  },
  {
    id: 'ukrainian.spelling.capital_letters',
    subject: 'ukrainian',
    strand: 'spelling',
    title: 'Велика літера',
    description: 'Пише з великої літери власні назви різних типів.',
    gradeBand: [2, 5],
    prerequisites: [],
    baseDifficulty: 900,
    generators: ['ukr.capitalLetters'],
  },

  // ── Морфологія ──────────────────────────────────────────────────────────
  {
    id: 'ukrainian.morphology.parts_of_speech',
    subject: 'ukrainian',
    strand: 'morphology',
    title: 'Частини мови',
    description: 'Визначає частину мови за питанням і значенням.',
    gradeBand: [3, 6],
    prerequisites: [],
    baseDifficulty: 950,
    foundational: true,
    generators: ['ukr.partsOfSpeech'],
  },
  {
    id: 'ukrainian.morphology.word_formation',
    subject: 'ukrainian',
    strand: 'morphology',
    title: 'Будова слова',
    description: 'Виділяє корінь, префікс, суфікс, закінчення.',
    gradeBand: [3, 6],
    prerequisites: [],
    baseDifficulty: 1020,
    generators: ['ukr.wordFormation'],
  },
  {
    id: 'ukrainian.morphology.noun_cases',
    subject: 'ukrainian',
    strand: 'morphology',
    title: 'Відмінки іменників',
    description: 'Визначає відмінок і правильно відмінює іменники.',
    gradeBand: [4, 6],
    prerequisites: ['ukrainian.morphology.parts_of_speech'],
    baseDifficulty: 1130,
    foundational: true,
    generators: ['ukr.nounCases'],
  },
  {
    id: 'ukrainian.morphology.adjective',
    subject: 'ukrainian',
    strand: 'morphology',
    title: 'Прикметник',
    description: 'Узгоджує прикметник з іменником, утворює ступені порівняння.',
    gradeBand: [4, 6],
    prerequisites: ['ukrainian.morphology.parts_of_speech'],
    baseDifficulty: 1080,
    generators: ['ukr.adjective'],
  },
  {
    id: 'ukrainian.morphology.verb',
    subject: 'ukrainian',
    strand: 'morphology',
    title: 'Дієслово',
    description: 'Визначає час, особу, число дієслова; знає неозначену форму.',
    gradeBand: [4, 6],
    prerequisites: ['ukrainian.morphology.parts_of_speech'],
    baseDifficulty: 1100,
    generators: ['ukr.verb'],
  },
  {
    id: 'ukrainian.morphology.pronoun_numeral',
    subject: 'ukrainian',
    strand: 'morphology',
    title: 'Займенник і числівник',
    description: 'Розпізнає займенники й числівники, правильно їх вживає.',
    gradeBand: [4, 6],
    prerequisites: ['ukrainian.morphology.parts_of_speech'],
    baseDifficulty: 1110,
    generators: ['ukr.pronounNumeral'],
  },

  // ── Синтаксис ───────────────────────────────────────────────────────────
  {
    id: 'ukrainian.syntax.sentence_parts',
    subject: 'ukrainian',
    strand: 'syntax',
    title: 'Члени речення',
    description: 'Знаходить підмет і присудок, розрізняє другорядні члени.',
    gradeBand: [4, 6],
    prerequisites: ['ukrainian.morphology.parts_of_speech'],
    baseDifficulty: 1120,
    generators: ['ukr.sentenceParts'],
  },
  {
    id: 'ukrainian.syntax.punctuation',
    subject: 'ukrainian',
    strand: 'syntax',
    title: 'Розділові знаки',
    description: 'Ставить кому при однорідних членах, звертанні, у складному реченні.',
    gradeBand: [4, 6],
    prerequisites: ['ukrainian.syntax.sentence_parts'],
    baseDifficulty: 1190,
    generators: ['ukr.punctuation'],
  },

  // ── Лексика ─────────────────────────────────────────────────────────────
  {
    id: 'ukrainian.lexicon.synonyms_antonyms',
    subject: 'ukrainian',
    strand: 'lexicon',
    title: 'Синоніми й антоніми',
    description: 'Добирає синоніми та антоніми, відчуває відтінки значення.',
    gradeBand: [3, 6],
    prerequisites: [],
    baseDifficulty: 970,
    generators: ['ukr.synonymsAntonyms'],
  },
  {
    id: 'ukrainian.lexicon.polysemy',
    subject: 'ukrainian',
    strand: 'lexicon',
    title: 'Багатозначні слова й омоніми',
    description: 'Розуміє значення слова в контексті, розрізняє омоніми.',
    gradeBand: [4, 6],
    prerequisites: ['ukrainian.lexicon.synonyms_antonyms'],
    baseDifficulty: 1100,
  },
  {
    id: 'ukrainian.lexicon.phraseology',
    subject: 'ukrainian',
    strand: 'lexicon',
    title: 'Фразеологізми',
    description: 'Розуміє й доречно вживає сталі вирази.',
    gradeBand: [4, 6],
    prerequisites: [],
    baseDifficulty: 1120,
    generators: ['ukr.phraseology'],
  },

  // ── Читання і література ────────────────────────────────────────────────
  {
    id: 'ukrainian.reading.comprehension',
    subject: 'ukrainian',
    strand: 'reading',
    title: 'Читання з розумінням',
    description: 'Витягує з тексту явну і приховану інформацію, робить висновок.',
    gradeBand: [3, 6],
    prerequisites: [],
    baseDifficulty: 1050,
    foundational: true,
  },
  {
    id: 'ukrainian.reading.main_idea',
    subject: 'ukrainian',
    strand: 'reading',
    title: 'Головна думка тексту',
    description: 'Визначає тему й головну думку, добирає заголовок.',
    gradeBand: [3, 6],
    prerequisites: ['ukrainian.reading.comprehension'],
    baseDifficulty: 1130,
  },
  {
    id: 'ukrainian.literature.authors_works',
    subject: 'ukrainian',
    strand: 'literature',
    title: 'Автори і твори',
    description: 'Знає ключових українських авторів та їхні твори за програмою.',
    gradeBand: [4, 6],
    prerequisites: [],
    baseDifficulty: 1150,
  },
];
